import React, {
  Fragment,
  useCallback,
  useRef,
  useState,
  useEffect
} from 'react'
import { arrayOf, oneOfType, bool, func, shape, node } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { selectLabels } from '../../redux/configuration/selectors'
import {
  setDepartureRoute,
  setReturnRoute,
  setForcesBooking
} from '../../redux/ferryRouteSelections/actions'
import {
  selectDepartureDate,
  selectDepartureRoute,
  selectReturnDate,
  selectReturnRoute,
  selectForcesBooking
} from '../../redux/ferryRouteSelections/selectors'
import {
  clearPromoValidation,
  fetchPromoInformation
} from '../../redux/promo/actions'
import * as UserSelectionActions from '../../redux/userSelections/actions'
import { selectCurrentBookingPromoState } from '../../redux/selectors/selectCurrentBookingPromoState'
import FerryRoutesFormSelect from './elements/FerryRoutesFormSelect'
import FerryRoutesFormDateRangePicker from './elements/dates/FerryRoutesFormDateRangePicker'
import ValidatedPromoInput from '../ValidatedPromoInput/ValidatedPromoInput'
import WarningBanner from '../WarningBanner/WarningBanner'
import Checkbox from '../Checkbox/Checkbox'
import ModalForm from '../ModalForm'
import AcknowledgmentModal from '../AcknowledgmentModal/AcknowledgmentModal'

const FerryRoutesRoundTripForm = ({
  availabilityErrors,
  ferryRoutes,
  isPairRoutes,
  submit,
  enableRouteSelection
}) => {
  const dispatch = useDispatch()
  const activeDepartureRoute = useSelector(selectDepartureRoute)
  const activeReturnRoute = useSelector(selectReturnRoute)
  const departureDate = useSelector(selectDepartureDate)
  const returnDate = useSelector(selectReturnDate)
  const labels = useSelector(selectLabels)
  const forcesBooking = useSelector(selectForcesBooking)
  const promoInputState = useSelector(selectCurrentBookingPromoState)
  const [showModal, setShowModal] = useState(false)
  const [scrollButtom, setScrollButtom] = useState(false)
  const focusRef = useRef(null)
  const labelRef = useRef(null)
  let isSubmitEnabled = false

  useEffect(() => {
    const link = labelRef?.current?.querySelector('a')
    if (link) {
      link.setAttribute(
        'aria-label',
        'Canadian Forces eligibility requirements'
      )
    }
  }, [labelRef, forcesBooking])

  const focusInput = useCallback(() => {
    if (focusRef.current) {
      focusRef.current.focus()
    }
  }, [])

  if (
    activeDepartureRoute &&
    activeDepartureRoute.code &&
    activeReturnRoute &&
    activeReturnRoute.code &&
    departureDate &&
    returnDate &&
    (promoInputState.valid === true ||
      promoInputState.valid === null ||
      promoInputState.code === '')
  ) {
    isSubmitEnabled = true
  }
  if (promoInputState.valid && forcesBooking) {
    isSubmitEnabled = false
  }

  const handleDepartureRoute = routeData => {
    if (isPairRoutes) {
      if (String(routeData.code) !== String(activeDepartureRoute.code)) {
        if (activeDepartureRoute && activeDepartureRoute.code) {
          dispatch(setReturnRoute(activeDepartureRoute))
          dispatch(
            UserSelectionActions.setReturnRoute(activeDepartureRoute.code)
          )
        } else {
          const firstAvailableRoute = ferryRoutes.find(
            route => route.code !== routeData.code
          )
          if (firstAvailableRoute) {
            dispatch(setReturnRoute(firstAvailableRoute))
            dispatch(
              UserSelectionActions.setReturnRoute(firstAvailableRoute.code)
            )
          }
        }
      }
    }
    dispatch(setDepartureRoute(routeData))

    dispatch(setReturnRoute(null))
    dispatch(UserSelectionActions.setReturnRoute(null))

    dispatch(UserSelectionActions.setDepartureRoute(routeData.code))
    dispatch(clearPromoValidation())
  }

  const handleCheckBoxClick = event => {
    const isChecked = event.target.checked
    if (!isChecked) {
      dispatch(setForcesBooking(false))
      dispatch(UserSelectionActions.setIsForcesMember(false))
    } else {
      setShowModal(true)
    }
  }

  const handleScroll = event => {
    const el = event.target
    const scrollHeightThreshold = 10
    const atBottom =
      el.scrollHeight - el.scrollTop <= el.clientHeight + scrollHeightThreshold
    setScrollButtom(atBottom)
  }

  const handleAcknowledge = () => {
    setShowModal(false)
    dispatch(setForcesBooking(true))
    dispatch(UserSelectionActions.setIsForcesMember(true))
  }

  const handleReturnRoute = routeData => {
    if (isPairRoutes) {
      if (String(routeData.code) !== String(activeReturnRoute.code)) {
        if (activeReturnRoute && activeReturnRoute.code) {
          dispatch(setDepartureRoute(activeReturnRoute))
          dispatch(
            UserSelectionActions.setDepartureRoute(activeReturnRoute.code)
          )
        } else {
          const firstAvailableRoute = ferryRoutes.find(
            route => route.code !== routeData.code
          )
          if (firstAvailableRoute) {
            dispatch(setDepartureRoute(firstAvailableRoute))
            dispatch(
              UserSelectionActions.setDepartureRoute(firstAvailableRoute.code)
            )
          }
        }
      }
    }
    dispatch(setReturnRoute(routeData))
    dispatch(UserSelectionActions.setReturnRoute(routeData.code))
    dispatch(clearPromoValidation())
  }

  let returnRoutes = ferryRoutes

  if (activeDepartureRoute?.crossings) {
    const validReturnRoutes = []
    activeDepartureRoute.crossings.forEach(c => {
      if (c.supplierCode !== activeDepartureRoute.code) {
        validReturnRoutes.push(c.supplierCode)
      }
    })
    returnRoutes = returnRoutes.filter(
      r => validReturnRoutes.indexOf(r.code) > -1
    )
  } else {
    returnRoutes = []
  }

  return (
    <div className="ferry-routes__form-round-trip">
      {!isEmpty(ferryRoutes) && (
        <div className="ferry-routes__form-routes">
          <FerryRoutesFormSelect
            activeRoute={activeDepartureRoute}
            ferryRoutes={ferryRoutes}
            fieldLabel={labels.departureRoute}
            toggleLabel={labels.change}
            updateRouteEvent={handleDepartureRoute}
            disableToggle={!enableRouteSelection}
          />
          <FerryRoutesFormSelect
            activeRoute={activeReturnRoute}
            ferryRoutes={returnRoutes}
            fieldLabel={labels.returnRoute}
            toggleLabel={labels.change}
            updateRouteEvent={handleReturnRoute}
            disableToggle={!enableRouteSelection || returnRoutes.length === 0}
          />
        </div>
      )}

      <FerryRoutesFormDateRangePicker
        ferryLabels={labels}
        onFocusInput={focusInput}
        returnDateOnly={!enableRouteSelection}
        onChange={() => dispatch(clearPromoValidation())}
      />

      <div className="ferry-routes__validated-promo-container">
        <ValidatedPromoInput
          labels={labels}
          refs={focusRef}
          handleBlur={v => dispatch(UserSelectionActions.setPromoCode(v))}
          submitValidation={code => {
            return dispatch(fetchPromoInformation(code)).then(isValid => {
              return isValid
            })
          }}
          clearValidation={() => dispatch(clearPromoValidation())}
          {...promoInputState}
        />
      </div>

      <div className="ferry-routes__forces-input-container">
        <Checkbox
          name={'canadianMilitary'}
          value={forcesBooking}
          label={labels.canadianForcesMember}
          disabled={false}
          onChange={handleCheckBoxClick}
          formikCheckbox={false}
        />
      </div>
      {forcesBooking && labels.canadianForcesMemberElig1 && (
        <p
          className="cfid-disclaimer"
          ref={labelRef}
          dangerouslySetInnerHTML={{ __html: labels.canadianForcesMemberElig1 }}
        ></p>
      )}

      {showModal && (
        <ModalForm
          modalClasses={'modal--smed'}
          isOpen={showModal}
          closeModal={() => setShowModal(false)}
          formTitle={' '}
        >
          <AcknowledgmentModal
            handleScroll={handleScroll}
            labels={labels}
            handleAcknowledge={handleAcknowledge}
            scrollButtom={scrollButtom}
          />
        </ModalForm>
      )}

      {availabilityErrors?.length ? (
        <Fragment>
          {availabilityErrors.map((error, i) => (
            <WarningBanner key={error + i}>{error}</WarningBanner>
          ))}
        </Fragment>
      ) : null}
      {promoInputState.valid && forcesBooking && (
        <WarningBanner>{labels.discountCannotBeCombined}</WarningBanner>
      )}
      <button
        className="ferry-routes__form-submit btn btn-primary large-primary-btn"
        onClick={submit}
        disabled={!isSubmitEnabled}
        data-test="btn-submit"
      >
        {labels.continueToPassengerInfoBtn} →
      </button>
    </div>
  )
}

FerryRoutesRoundTripForm.propTypes = {
  availabilityErrors: oneOfType([bool, arrayOf(node)]),
  openModal: func.isRequired,
  closeModal: func.isRequired,
  modalIsOpen: shape().isRequired,
  ferryRoutes: arrayOf(shape()).isRequired,
  isPairRoutes: bool,
  submit: func.isRequired,
  enableRouteSelection: bool.isRequired
}

export default FerryRoutesRoundTripForm
